import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Info } from 'lucide-react';

interface AdProps {
    /**
     * AdSense ad unit slot ID (e.g., '1234567890')
     * Optional - if not provided, uses auto ads
     */
    adSlot?: string;
    /**
     * Ad format: 'auto', 'rectangle', 'horizontal', 'vertical', or custom dimensions
     */
    format?: 'auto' | 'rectangle' | 'horizontal' | 'vertical' | string;
    /**
     * Ad size: 'responsive' or specific dimensions like '300x250'
     */
    size?: 'responsive' | string;
    /**
     * Additional CSS class name
     */
    className?: string;
    /**
     * Style object
     */
    style?: React.CSSProperties;
}

/**
 * Google AdSense Ad Component
 *
 * Pure function implementation of AdSense ads.
 * Uses environment variable VITE_GOOGLE_ADSENSE_ID for publisher ID.
 * Set VITE_SHOW_ADS=false to hide all ads.
 */
const Ad: React.FC<AdProps> = ({
    adSlot = '5391792359',
    format = 'auto',
    size = 'responsive',
    className = '',
    style = {}
}) => {
    const showAds = import.meta.env.VITE_SHOW_ADS !== 'false';
    const publisherId = import.meta.env.VITE_GOOGLE_ADSENSE_ID;
    const adRef = useRef<HTMLDivElement>(null);
    const initializedRef = useRef(false);

    // Enable test mode on localhost to see ads during development
    const isLocalhost = typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.startsWith('192.168.'));
    const enableTestMode = isLocalhost;

    // Initialize AdSense ad only once per component instance
    useEffect(() => {
        if (!showAds || !publisherId || initializedRef.current) {
            return;
        }

        // Wait for the ins element to be in the DOM
        const insElement = adRef.current?.querySelector('.adsbygoogle') as HTMLElement;
        if (!insElement) {
            return;
        }

        // Check if ad has already been initialized (has adsbygoogle data attribute)
        if (insElement.getAttribute('data-adsbygoogle-status')) {
            initializedRef.current = true;
            return;
        }

        // Initialize the ad
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            initializedRef.current = true;
        } catch (e) {
            console.error('AdSense initialization error:', e);
        }
    }, [showAds, publisherId]);

    // If ads are disabled, don't render anything
    if (!showAds) {
        return null;
    }

    if (!publisherId || publisherId.trim() === '') {
        return (
            <Box
                className={`ad-container ad-placeholder ${className}`.trim()}
                sx={{ ...style, p: 1, textAlign: 'center' }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Info size={16} />
                    Ad space - Configure VITE_GOOGLE_ADSENSE_ID in .env file
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={adRef}
            className={`ad-container ${className}`.trim()}
            sx={style}
        >
            <ins
                className="adsbygoogle"
                style={{ display: 'block', ...style }}
                data-ad-client={publisherId}
                data-ad-slot={adSlot}
                data-ad-format={format === 'auto' ? 'auto' : undefined}
                data-full-width-responsive={size === 'responsive' ? 'true' : 'false'}
                data-adtest={enableTestMode ? 'on' : undefined}
            />
        </Box>
    );
};

export default Ad;
